'use client';

import Link from 'next/link';
import { Calendar, Clock, ArrowRight, Tag, Eye } from 'lucide-react';
import { memo, useCallback } from 'react';

// Memoize date formatting to avoid recalculation
const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

function BlogCard({ post, featured = false }) {
  if (featured) {
    return (
      <Link
        href={`/blog/${post.slug}`}
        className="group relative block overflow-hidden rounded-2xl bg-gradient-to-br from-blue-600 via-blue-500 to-cyan-500 p-1 transition-all duration-300 hover:shadow-2xl hover:scale-[1.02]"
      >
        <div className="relative h-full rounded-xl bg-white dark:bg-gray-900 p-8 md:p-10 text-left space-y-4">
          <div className="flex items-center gap-4 text-sm flex-wrap">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 font-medium rounded-lg">
              <Tag size={14} />
              {post.category}
            </span>
            <div className="flex items-center gap-4 text-gray-600 dark:text-gray-400">
              {post.views !== undefined && (
                <span className="flex items-center gap-1.5">
                  <Eye size={14} />
                  {post.views.toLocaleString()} {post.views === 1 ? 'view' : 'views'}
                </span>
              )}
              <span className="flex items-center gap-1.5">
                <Clock size={14} />
                {post.readTime}
              </span>
            </div>
          </div>

          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white leading-tight group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
            {post.title}
          </h2>

          <p className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed line-clamp-3">
            {post.excerpt}
          </p>

          <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-800">
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <Calendar size={14} />
              <time dateTime={post.date}>{formatDate(post.date)}</time>
            </div>
            <span className="inline-flex items-center gap-2 text-blue-600 dark:text-blue-400 font-semibold group-hover:gap-3 transition-all">
              Read Article <ArrowRight size={18} />
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      href={`/blog/${post.slug}`}
      className="group block h-full rounded-2xl bg-white dark:bg-gray-900 shadow-md shadow-slate-200/50 dark:shadow-[0_4px_20px_rgba(0,0,0,0.4)] hover:shadow-xl border border-slate-200 dark:border-gray-800 ring-1 ring-black/[0.03] dark:ring-white/[0.06] hover:border-blue-400 dark:hover:border-blue-500 transition-all duration-300 overflow-hidden"
    >
      <div className="p-6 h-full flex flex-col text-left">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-sm font-medium rounded-lg">
            <Tag size={14} />
            {post.category}
          </span>
          <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
            {post.views !== undefined && (
              <span className="flex items-center gap-1.5">
                <Eye size={14} />
                {post.views.toLocaleString()}
              </span>
            )}
            <span className="flex items-center gap-1.5">
              <Clock size={14} />
              {post.readTime}
            </span>
          </div>
        </div>

        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors line-clamp-2">
          {post.title}
        </h3>

        <p className="text-gray-600 dark:text-gray-300 mb-4 flex-grow line-clamp-3">
          {post.excerpt}
        </p>

        <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-800">
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <Calendar size={14} />
            <time dateTime={post.date}>{formatDate(post.date)}</time>
          </div>
          <span className="inline-flex items-center gap-2 text-blue-600 dark:text-blue-400 font-medium group-hover:gap-3 transition-all">
            Read More <ArrowRight size={16} />
          </span>
        </div>
      </div>
    </Link>
  );
}

// Memo the component to prevent unnecessary re-renders
export default memo(BlogCard);
